import { BRAND } from "constants/brand";
import useCity from "hooks/useCity";
import { FontAwesomeIcon as NoticeIcon } from "@fortawesome/react-fontawesome";
import { faClock } from "@fortawesome/free-solid-svg-icons";
// The notice of the orders page: the same strip, for the same job (it says
// that the table shows a special view, and gives the way back).
import "components/Order/RunningOrdersToggle/runningNotice.scss";
import { Ring } from "@uiball/loaders";
import "assets/styles/table.scss";
import { useGetVendors, useUpdateVendor, useRemoveVendor } from "hooks/useVendors";
import { useDispatch, useSelector } from "react-redux";
import Vendor from "./Vendor/Vendor";
import Pagination from "@mui/material/Pagination";
import Stack from "@mui/material/Stack";
import {
    filterDateByName,
    setPage,
    filterDateByCity,
    filterByAppStatus,
    filterByDashboardStatus,
    setOrderView,
    setSort,
} from "store/vendorSlice";
import { useState, useRef, useEffect } from "react";
import { Toast } from "primereact/toast";
import DeleteItem from "components/Ui/DeleteItem/DeleteItem";
import SearchInput from "components/Ui/SearchInput/SearchInput";
import { NavLink, useNavigate } from "react-router-dom";
import { Form } from "react-bootstrap";
import VendorScheduleModal from "./EditVendor/VendorScheduleModal";
import VendorScheduleSingleModal from "./EditVendor/VendorScheduleSingleModal";
import CopyCosts from "./EditVendor/CopyCosts";
import useVendorCategories from "hooks/useVendorCategories";
import { updateVendorDisplayRank, updateVendorOrderPercentage } from "services/vendors";
import TableColumnsMenu from "components/Ui/TableColumnsMenu/TableColumnsMenu";
import TableHead from "components/Ui/TableHead/TableHead";
import useTableColumnsConfig from "hooks/useTableColumnsConfig";
import ChangeVendorPassword from "./ChangeVendorPassword/ChangeVendorPassword";
import { api } from "api";
import { impersonateUser as impersonateUserURL } from "api/apiURLs";
import { authSliceActions } from "store/authSlice";
import { getStoredToken, getStoredRefreshToken } from "utils/auth";
import { toggleBlockUserService } from "services/user";
import VendorWalletModal from "./VendorWallet/VendorWalletModal";
import { useSaveVendorWalletSettings } from "hooks/useVendorWallet";

/**
 * Exactly the API's allow-list in util/listSort.js.
 *
 * `rating` and `completedOrders` are absent, and neither can be added here
 * alone. Rating lives on `vendors`, which the paginating subquery cannot order
 * by — offering it returned a page of unrated restaurants. The order count is a
 * separate aggregate over the ids on the page. Both need an API change first.
 */
const SORTABLE_COLUMNS = ["id", "name", "createdAt"];

/**
 * Minutes until the customer's order changes. The API rotates the restaurants
 * that are not pinned in 30-minute windows counted from the Unix epoch
 * (`api/src/util/vendorDisplayOrder.js`), so the next change is the next
 * multiple of 30 minutes, on every clock.
 */
const ROTATION_MS = 30 * 60_000;
const minutesToNextRotation = () =>
    Math.max(1, Math.ceil((ROTATION_MS - (Date.now() % ROTATION_MS)) / 60_000));

const COLUMNS = [
    // THE SAME GROUPS, IN THE SAME ORDER, AS THE PEOPLE TABLES — see
    // COMMON_COLUMNS and the drivers columns in components/Users/Users.js, and
    // AGENTS.md, "Table columns follow one order". Identity, then the
    // arrangement, then place, then live state, then the record, then money and
    // limits, then the block control, then the actions.
    // identity
    { id: "select", label: "", width: "30px", pinned: true },
    { id: "id", label: "#", width: "10px" },
    { id: "name", label: "الاسم", width: "100px" },
    // arrangement: what kind of restaurant, and the rate agreed with it
    { id: "type", label: "النوع", width: "100px" },
    { id: "categories", label: "التصنيف", width: "100px" },
    { id: "commissionPercentage", label: "النسبة", width: "120px" },
    // The place in the customer lists. Arrangement, like the rate: it is what
    // was decided about the restaurant, not what it is doing now.
    {
        id: "displayRank",
        label: "الترتيب",
        hint: "مكان المطعم في قوائم الزبون. المثبّت يظهر أولًا بالرقم الأصغر، والباقي يتغير ترتيبه كل 30 دقيقة. المطاعم المفتوحة قبل المغلقة دائمًا",
        width: "110px",
    },
    // place
    { id: "city", label: "المدينه", width: "80px" },
    { id: "areas", label: "المناطق", width: "50px" },
    { id: "address", label: "العنوان", width: "80px" },
    // live state: is it trading right now
    { id: "status", label: "الحاله", width: "100px" },
    { id: "schedule", label: "مواعيد العمل", width: "100px" },
    { id: "appStatus", label: "تطبيق المطعم", width: "115px" },
    { id: "dashboardStatus", label: "لوحة التحكم", width: "115px" },
    // record
    { id: "completedOrders", label: "طلبات مكتملة", width: "100px" },
    { id: "rating", label: "التقييم", width: "90px" },
    // MONEY AND LIMITS, beside the block control — the same slot, ids and
    // labels as the drivers table (api#147). The mode first, then the balance:
    // a restaurant on the wallet at ₪0 or less is closed, and that reads only
    // when the two sit side by side.
    { id: "walletMode", label: "المحفظة", width: "110px" },
    { id: "walletBalance", label: "الرصيد", width: "120px" },
    // account state, then actions — always last
    { id: "block", label: "الحظر", width: "100px" },
    { id: "actions", label: "الاعدادات", width: "50px" },
];

/**
 * Vendor list page with server-side pagination and fast inline admin actions.
 * Filters live in Redux so refetches stay consistent across pagination and mutations.
 *
 * Keep COLUMNS in semantic group order. Insert a new field in its group, not
 * after الحظر or الاعدادات, so new fields do not appear as accidental tails.
 */
const Vendors = () => {
    const [selectedVendors, setSelectedVendors] = useState([]);
    const [showScheduleModal, setShowScheduleModal] = useState(false);
    const [showCopyCosts, setShowCopyCosts] = useState(false);

    const [showDelete, setShowDelete] = useState(false);
    const [selectedVendor, setSelectedVendor] = useState(null);
    const [showChangePassword, setShowChangePassword] = useState(false);
    // The restaurant whose wallet modal is open (admin#41). `id` is the users.id.
    const [walletVendor, setWalletVendor] = useState(null);
    const saveWalletSettings = useSaveVendorWalletSettings();
    const getVendorsQuery = useGetVendors();

    /**
     * نقدي / محفظة for one restaurant (api#147).
     *
     * Switching on at ₪0 or less closes the restaurant at once, so the message
     * says so — the status chip will change without anybody having touched it.
     * Not rethrown: the cell closes either way, and the refetch shows the
     * server's state.
     */
    const handleSaveWalletMode = async (vendor, enabled) => {
        try {
            const result = await saveWalletSettings.mutateAsync({ userId: vendor.id, enabled });
            showMessage(
                "success",
                "تم الحفظ",
                result.wallet?.blocked
                    ? "المطعم على المحفظة الآن، وأُغلق لأن رصيده صفر أو أقل. اشحن المحفظة، ثم افتح المطعم."
                    : enabled
                      ? "المطعم على نظام المحفظة الآن"
                      : "المطعم على النظام النقدي الآن",
            );
        } catch (err) {
            showMessage("error", "تعذر الحفظ", err.response?.data?.message || "حدث خطأ غير متوقع");
        }
    };
    const updateVendorMutation = useUpdateVendor();
    const removeVendorMutation = useRemoveVendor();
    const vendors = useSelector((state) => state.vendor.vendors);
    const pages = useSelector((state) => state.vendor.pages);
    const currentPage = useSelector((state) => state.vendor.currentPage);
    const cityFilter = useSelector((state) => state.vendor.cityFilter);
    const nameFilter = useSelector((state) => state.vendor.nameFilter);
    const appStatusFilter = useSelector((state) => state.vendor.appStatusFilter);
    const dashboardStatusFilter = useSelector((state) => state.vendor.dashboardStatusFilter);
    const orderView = useSelector((state) => state.vendor.orderView);
    const sortBy = useSelector((state) => state.vendor.sortBy);
    const sortDir = useSelector((state) => state.vendor.sortDir);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const toast = useRef(null);
    // `useCity` LOADS the cities. Reading `state.city.cities` alone left the
    // city filter empty for anyone who opened this page first: nothing had
    // fetched the list yet.
    const { cities } = useCity();
    const adminInfo = useSelector((state) => state.auth.userData);
    const { categories } = useVendorCategories();
    const { orderedColumns, visibleColumns, hiddenSet, toggleColumn, moveColumn, reset } =
        useTableColumnsConfig("vendors", COLUMNS);

    const [showVendorScheduleModal, setShowVendorScheduleModal] = useState(false);
    const [activeVendorId, setActiveVendorId] = useState(null);
    const categoryOptions = (categories || []).map((category) => ({
        value: category.id,
        label: category.name,
    }));

    // The name search debounces inside <SearchInput>; dispatching the term to
    // the vendor slice resets the page and refetches via useGetVendors.
    const handleSearch = (value) => dispatch(filterDateByName(value));

    const openScheduleModal = (vendorId) => {
        setActiveVendorId(vendorId);
        setShowVendorScheduleModal(true);
    };

    useEffect(() => {
        if (!adminInfo?.super_admin) {
            dispatch(filterDateByCity(adminInfo.cityId));
        }
    }, [adminInfo, dispatch]);

    const handleChangePage = (event, newPage) => {
        dispatch(setPage(newPage));
    };

    const showMessage = (type, head, content) => {
        toast.current.show({
            severity: type,
            summary: head,
            detail: content,
            life: 3000,
        });
    };

    /**
     * Save one restaurant's commission rate from the table.
     *
     * `null` clears it, putting the restaurant back on its city's default.
     * That is a real action, so an empty box is allowed rather than refused.
     */
    const handleSavePercentage = async (vendor, percentage) => {
        try {
            await updateVendorOrderPercentage(vendor.id, percentage);
            showMessage(
                "success",
                "تم الحفظ",
                percentage === null ? "أصبح المطعم على نسبة المدينة" : "تم تحديث النسبة",
            );
            await getVendorsQuery.refetch?.();
        } catch (err) {
            showMessage("error", "تعذر الحفظ", err.response?.data?.message || "حدث خطأ غير متوقع");
        }
    };

    /**
     * الترتيب — pins a restaurant to a place in the customer lists, or removes
     * the pin with `null`.
     */
    const handleSaveDisplayRank = async (vendor, rank) => {
        try {
            await updateVendorDisplayRank(vendor.id, rank);
            showMessage(
                "success",
                "تم الحفظ",
                rank === null
                    ? "أُزيل التثبيت، وعاد المطعم للترتيب التلقائي"
                    : `ثُبّت المطعم في المكان ${rank}`,
            );
            await getVendorsQuery.refetch?.();
        } catch (err) {
            showMessage("error", "تعذر الحفظ", err.response?.data?.message || "حدث خطأ غير متوقع");
        }
    };

    const searchChangeCity = (e) => {
        dispatch(filterDateByCity(e.target.value));
    };
    const setSelectedVendorValue = (vendor) => {
        setSelectedVendor(vendor);
    };

    const toggleDelete = () => {
        setShowDelete((pre) => (pre = !pre));
    };

    /**
     * انتحال هوية — the same flow the people pages use.
     *
     * Takes `vendor.id`, which on this row is the **users.id**, NOT the
     * vendors.id that `vendor.vendorId` carries. `POST admin/impersonate/:userId`
     * wants a user id. See AGENTS.md, "The id trap".
     *
     * Lands on the home page. This screen is admin-only, so an admin who
     * impersonates a restaurant from here would otherwise sit on a page that
     * session cannot render, whose next refetch runs as the restaurant.
     */
    const handleImpersonate = async (vendor) => {
        try {
            const response = await api.post(`${impersonateUserURL}/${vendor.id}`, {
                supportsRefresh: true,
            });
            dispatch(
                authSliceActions.startImpersonation({
                    impersonatedUser: response.data.user,
                    originalToken: getStoredToken(),
                    originalRefreshToken: getStoredRefreshToken(),
                }),
            );
            showMessage("info", "انتحال هوية", `أنت الآن تتصفح كـ ${vendor.name}`);
            navigate("/");
        } catch (err) {
            showMessage("error", "هناك خطأ", err.response?.data?.message || "حدث خطأ غير متوقع");
        }
    };

    /**
     * الحظر — one click blocks, un-blocks, and restores a removed account,
     * because the server treats the last two as the same un-block.
     *
     * Again `vendor.id`: `PATCH user/toggle-block/:id` reads the users table.
     * The list is refetched afterwards because the row's own state is what the
     * button draws.
     */
    const handleToggleBlock = async (vendor) => {
        try {
            const response = await toggleBlockUserService(vendor.id);
            const wasArchived = Boolean(vendor?.archivedAt);

            showMessage(
                response?.user?.isBlocked ? "warn" : "success",
                response?.user?.isBlocked
                    ? "تم الحظر"
                    : wasArchived
                      ? "تم الإرجاع"
                      : "تم إلغاء الحظر",
                response?.message || "تم تحديث حالة الحساب",
            );
            await getVendorsQuery.refetch?.();
        } catch (err) {
            showMessage("error", "هناك خطأ", err.response?.data?.message || "حدث خطأ غير متوقع");
        }
    };

    // Two outcomes, both successful: a store with no orders, products,
    // statements or videos is really deleted; one with history is archived
    // (hidden from the app and from this list, records kept, reversible from
    // الحسابات). The API decides which — it is the only side that can count —
    // and returns the Arabic reason, so this shows the server's message rather
    // than inventing one that might describe the wrong outcome.
    const confirmDelete = async () => {
        try {
            const result = await removeVendorMutation.mutateAsync(selectedVendor.id);

            showMessage(
                result?.deleted ? "warn" : "info",
                result?.deleted ? "تم الحذف" : "تم الإخفاء",
                result?.message || "تمت إزالة المطعم",
            );
            toggleDelete();
        } catch (err) {
            console.error("Error deleting vendor:", err);
            showMessage("error", "هناك خطأ", err.response?.data?.message || "حدث خطأ غير متوقع");
        }
    };

    return (
        <div className="items">
            <Toast style={{ direction: "rtl" }} ref={toast} position="top-left" />

            {getVendorsQuery.loading ? (
                <div className="loadingSpinner">
                    <Ring size={40} lineWeight={5} speed={2} color={BRAND} />
                </div>
            ) : (
                <>
                    {/* One row, matching the users page: filters, then the
                        actions, then the columns menu pushed to the far end.
                        These were two stacked `.controls` rows — actions above,
                        filters below — which read as two unrelated toolbars and
                        pushed the table down by a whole row on every screen. */}
                    <div className="controls">
                        <div style={{ display: "flex", gap: "10px" }}>
                            {adminInfo?.super_admin ? (
                                <Form.Select value={cityFilter} onChange={searchChangeCity}>
                                    <option value={""}>المدينة: الكل</option>
                                    {cities?.map((user) => (
                                        <option key={user.id} value={user.id}>
                                            {user.name}
                                        </option>
                                    ))}
                                </Form.Select>
                            ) : null}
                            <Form.Select
                                value={appStatusFilter}
                                onChange={(e) => dispatch(filterByAppStatus(e.target.value))}
                                title="هل استلمنا إشارة من تطبيق المطعم مؤخراً؟"
                            >
                                <option value={""}>التطبيق: الكل</option>
                                <option value={"closed"}>التطبيق: مغلق</option>
                                <option value={"open"}>التطبيق: مفتوح</option>
                            </Form.Select>
                            {/* The second channel. The table has shown لوحة التحكم
                                as its own column for a while, but only the app
                                could be filtered on — so "which restaurants have
                                nobody watching either surface" was not askable. */}
                            <Form.Select
                                value={dashboardStatusFilter}
                                onChange={(e) => dispatch(filterByDashboardStatus(e.target.value))}
                                title="هل فتح المطعم لوحة التحكم مؤخراً؟"
                            >
                                <option value={""}>اللوحة: الكل</option>
                                <option value={"closed"}>اللوحة: مغلقة</option>
                                <option value={"open"}>اللوحة: مفتوحة</option>
                            </Form.Select>
                            {/* The customer's order, in the same row as the other
                                views of the table. */}
                            <Form.Select
                                value={orderView}
                                onChange={(e) => dispatch(setOrderView(e.target.value))}
                                title="رتّب الجدول كما يراه الزبون الآن، أو اعرض المثبّتة فقط"
                            >
                                <option value={""}>الترتيب: ترتيب اللوحة</option>
                                <option value={"customer"}>الترتيب: كما يراه الزبون الآن</option>
                                <option value={"pinned"}>الترتيب: المثبّتة فقط</option>
                            </Form.Select>
                            <SearchInput
                                initialValue={nameFilter || ""}
                                onSearch={handleSearch}
                                placeholder="بحث بالاسم، الهاتف أو رقم المطعم"
                                minWidth={200}
                            />
                        </div>
                        <div>
                            <NavLink to={"add"} className="button">
                                إضافه مطعم
                            </NavLink>
                            <button
                                className="button"
                                style={{ opacity: selectedVendors.length === 0 ? 0.6 : 1 }}
                                disabled={selectedVendors.length === 0}
                                onClick={() => setShowScheduleModal(true)}
                            >
                                تحديد مواعيد العمل
                            </button>
                            <button
                                className="button"
                                style={{ opacity: selectedVendors.length === 0 ? 0.6 : 1 }}
                                disabled={selectedVendors.length === 0}
                                onClick={() => setShowCopyCosts(true)}
                            >
                                نسخ تكاليف التوصيل
                            </button>
                        </div>
                        <div style={{ marginInlineStart: "auto" }}>
                            <TableColumnsMenu
                                orderedColumns={orderedColumns}
                                hiddenSet={hiddenSet}
                                toggleColumn={toggleColumn}
                                moveColumn={moveColumn}
                                reset={reset}
                            />
                        </div>
                    </div>
                    {orderView === "customer" ? (
                        <div className="running-notice" role="status">
                            <NoticeIcon icon={faClock} className="running-notice__icon" />
                            <span className="running-notice__text">
                                هذا ترتيب المطاعم كما يراه الزبون الآن: المفتوحة أولًا، ثم المثبّتة
                                بأرقامها، ثم الباقي بترتيب يتغير بعد{" "}
                                <bdi dir="ltr">{minutesToNextRotation()}</bdi> دقيقة.
                                {cityFilter
                                    ? ""
                                    : " اختر مدينة لترى قائمة مدينة واحدة كما يراها زبونها."}
                            </span>
                            <button
                                type="button"
                                className="running-notice__action"
                                onClick={() => dispatch(setOrderView(""))}
                            >
                                ترتيب اللوحة
                            </button>
                        </div>
                    ) : null}
                    <div className="table-container">
                        <table>
                            <TableHead
                                visibleColumns={visibleColumns}
                                sortableColumns={SORTABLE_COLUMNS}
                                sort={{ by: sortBy, dir: sortDir }}
                                onSortChange={(by, dir) =>
                                    dispatch(setSort({ sortBy: by, sortDir: dir }))
                                }
                                headerRenderers={{
                                    select: () => (
                                        <input
                                            type="checkbox"
                                            className="form-check-input"
                                            checked={
                                                vendors?.length > 0 &&
                                                vendors.every((v) =>
                                                    selectedVendors.includes(v.vendorId),
                                                )
                                            }
                                            onChange={(e) => {
                                                if (e.target.checked) {
                                                    setSelectedVendors((prev) => {
                                                        const pageIds = vendors.map(
                                                            (v) => v.vendorId,
                                                        );
                                                        return Array.from(
                                                            new Set([...prev, ...pageIds]),
                                                        );
                                                    });
                                                } else {
                                                    setSelectedVendors((prev) => {
                                                        const pageIds = vendors.map(
                                                            (v) => v.vendorId,
                                                        );
                                                        return prev.filter(
                                                            (id) => !pageIds.includes(id),
                                                        );
                                                    });
                                                }
                                            }}
                                        />
                                    ),
                                }}
                            />
                            <tbody>
                                {vendors ? (
                                    vendors.map((vendor) => (
                                        <Vendor
                                            key={vendor.id}
                                            vendor={vendor}
                                            visibleColumns={visibleColumns}
                                            updateVendor={(data) =>
                                                updateVendorMutation.mutateAsync(data)
                                            }
                                            categoryOptions={categoryOptions}
                                            showMessage={showMessage}
                                            setSelectedVendor={setSelectedVendor}
                                            setSelectedVendorValue={setSelectedVendorValue}
                                            toggleDelete={toggleDelete}
                                            onSavePercentage={handleSavePercentage}
                                            onSaveDisplayRank={handleSaveDisplayRank}
                                            onToggleBlock={handleToggleBlock}
                                            onImpersonate={handleImpersonate}
                                            onChangePassword={(v) => {
                                                setSelectedVendor(v);
                                                setShowChangePassword(true);
                                            }}
                                            selectedVendors={selectedVendors}
                                            setSelectedVendors={setSelectedVendors}
                                            openScheduleModal={openScheduleModal}
                                            onOpenWallet={setWalletVendor}
                                            onSaveWalletMode={handleSaveWalletMode}
                                        />
                                    ))
                                ) : (
                                    <h4>لا يوجد مطاعم!</h4>
                                )}
                            </tbody>
                        </table>
                    </div>
                    <Stack spacing={1}>
                        <Pagination
                            count={pages}
                            page={currentPage}
                            shape="rounded"
                            color={BRAND}
                            style={{ margin: "20px auto 0" }}
                            onChange={handleChangePage}
                        />
                    </Stack>

                    <DeleteItem
                        show={showDelete}
                        close={toggleDelete}
                        loading={getVendorsQuery.loading || removeVendorMutation.isPending}
                        confirm={confirmDelete}
                        message="سيتم حذف المطعم نهائيا إذا لم يكن عليه أي طلبات أو منتجات أو كشوفات. إذا كان له سجل، سيتم إخفاؤه من التطبيق مع الاحتفاظ بسجله، ويمكن إرجاعه من هذه الصفحة بالضغط على «مؤرشف» في عمود الحظر."
                    />
                    <VendorScheduleModal
                        show={showScheduleModal}
                        onClose={() => setShowScheduleModal(false)}
                        vendorIds={selectedVendors}
                    />
                    <VendorScheduleSingleModal
                        show={showVendorScheduleModal}
                        vendorId={activeVendorId}
                        onClose={() => setShowVendorScheduleModal(false)}
                    />
                    <ChangeVendorPassword
                        show={showChangePassword}
                        close={() => setShowChangePassword(false)}
                        showMessage={showMessage}
                        vendorId={selectedVendor?.id}
                        vendorName={selectedVendor?.name}
                    />
                    <VendorWalletModal
                        show={Boolean(walletVendor)}
                        close={() => setWalletVendor(null)}
                        vendor={walletVendor}
                        showMessage={showMessage}
                    />
                    <CopyCosts
                        show={showCopyCosts}
                        close={() => setShowCopyCosts(false)}
                        showMessage={showMessage}
                        selectedVendors={selectedVendors}
                    />
                </>
            )}
        </div>
    );
};
export default Vendors;
